/**
 * InvoiceDetailView - the body of S5, the detail of one invoice (SIH-17 I-8c,
 * TL A3; UX-T16 page 07): a definition list of the invoice's fields and a
 * selectable evidence block for escalation.
 *
 * Every value is the source's, shown verbatim (BR-1). A value the source does not
 * hold - null or the empty string - reads "Not recorded by the source" (BA copy),
 * on any status: a pending invoice's error code and message stay visible as that
 * line rather than disappearing. The status is the delivered one, with the same
 * one-word "unmapped" marker as S3 and S4 when its code cannot be mapped. There is
 * no export, email or retry action. Long values wrap.
 */

import type { ReactNode } from 'react'
import { formatTimestamp } from '@domain/dates'
import { STATUS_WORD } from '@domain/invoiceDetail'
import type { InvoiceRow } from '@domain/types'
import { NOT_RECORDED_TEXT, heldValue } from './absentValue'
import { Submitted } from './InvoiceCards'
import { ErrorCode, UNMAPPED_TEXT, hasUnmappedCode } from './InvoiceTable'

export const EVIDENCE_NOTE = 'Read-only. There is no export, email or retry action in this product.'

function Row({ label, testid, children }: { label: string; testid: string; children: ReactNode }) {
  return (
    <div className="invoice-detail-row">
      <dt>{label}</dt>
      <dd data-testid={testid}>{children}</dd>
    </div>
  )
}

function periodText(from: string, to: string): string {
  return from === to ? `Counted in period: ${from}` : `Counted in period: ${from} to ${to}`
}

/** The lines of the copy-out block, in the order the reader pastes them into an escalation. */
export function evidenceLines(row: InvoiceRow, now: Date): readonly string[] {
  const code = heldValue(row.resultCd)
  const submitted = heldValue(row.submissionTimestamp)
  return [
    `Invoice ${row.rectNo}`,
    `Error code ${code === null ? NOT_RECORDED_TEXT : code}`,
    `Submitted ${submitted === null ? NOT_RECORDED_TEXT : (formatTimestamp(submitted, now).absolute as string)}`,
  ]
}

export function InvoiceDetailView({
  row,
  dbName,
  from,
  to,
  now,
}: {
  readonly row: InvoiceRow
  readonly dbName: string
  readonly from: string
  readonly to: string
  readonly now: Date
}) {
  return (
    <>
      <section className="invoice-detail" aria-label="Invoice detail" data-testid="invoice-detail">
        <dl className="invoice-detail-list">
          <Row label="Sync status" testid="detail-status">
            <span className={`status-badge status-badge--${row.derivedStatus.toLowerCase()}`} data-testid="detail-status-word">
              {STATUS_WORD[row.derivedStatus]}
            </span>
            {hasUnmappedCode(row.resultCd) && (
              <>
                {' · '}
                <mark className="unmapped-marker" data-testid="detail-unmapped">
                  {UNMAPPED_TEXT}
                </mark>
              </>
            )}
          </Row>
          <Row label="Invoice identifier" testid="detail-id">
            {row.rectNo}
          </Row>
          <Row label="Customer" testid="detail-customer">
            {dbName}
          </Row>
          <Row label="TPIN / branch" testid="detail-branch">
            {`${row.tin} / ${row.bhfId}`}
          </Row>
          <Row label="Created" testid="detail-created">
            {/* The same timestamp cell as Submitted: absolute time, relative age, or the absent-value copy. */}
            <Submitted raw={row.createdDate} now={now} />
            <span className="invoice-detail-note">Created date decides the period. Status is as of now (aging stays visible).</span>
          </Row>
          <Row label="Submitted" testid="detail-submitted">
            <Submitted raw={row.submissionTimestamp} now={now} />
          </Row>
          <Row label="Period" testid="detail-period">
            {periodText(from, to)}
          </Row>
          <Row label="Error code" testid="detail-code">
            <ErrorCode code={row.resultCd} />
          </Row>
          <Row label="Error message" testid="detail-message">
            {heldValue(row.lastError) ?? NOT_RECORDED_TEXT}
          </Row>
        </dl>
      </section>
      <section className="invoice-evidence" aria-label="Evidence for escalation" data-testid="invoice-evidence">
        <h2 className="invoice-evidence-title">Evidence for escalation (select and copy)</h2>
        <pre className="invoice-evidence-text" data-testid="evidence-text">
          {evidenceLines(row, now).join('\n')}
        </pre>
        <p className="invoice-detail-note" data-testid="evidence-note">
          {EVIDENCE_NOTE}
        </p>
      </section>
    </>
  )
}
